import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';

const COLOR_HOTSPOT_WILL_OPEN = 'color-hotspot-will-open';
const OVERLAY_SELECTOR = '.color-hotspot-overlay';
const BODY_OVERLAY_OPEN_CLASS = 'color-hotspot-overlay-open';

/**
 * A custom element that manages a color hotspot dialog (no quick-add).
 * On mobile/touch, click opens dialog; dialog content is a link to the color metaobject URL.
 *
 * @typedef {object} ColorHotspotRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} trigger - The button element.
 * @property {HTMLAnchorElement} [colorLink] - The color link element (optional).
 *
 * @extends {Component<ColorHotspotRefs>}
 */
export class ColorHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  /** @type {(() => void) | null} */
  #pointerenterHandler = null;
  /** @type {((e: CustomEvent<{ source: ColorHotspotComponent }>) => void) | null} */
  #willOpenListener = null;
  /** @type {((e: MouseEvent) => void) | null} */
  #overlayClickHandler = null;
  /** @type {((e: MouseEvent) => void) | null} */
  #dialogLinkClickHandler = null;
  /** @type {((e: TouchEvent) => void) | null} */
  #dialogLinkTouchHandler = null;
  timer = /** @type {number | null} */ (null);

  /**
   * Get this component's overlay element (for mobile backdrop).
   * Each color-hotspot-component has its own overlay inside it.
   * @returns {HTMLElement | null}
   */
  #getOverlay() {
    return this.querySelector(OVERLAY_SELECTOR);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    this.#willOpenListener = (e) => {
      if (e.detail?.source === this) return;
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.refs?.dialog?.open) {
        this.closeDialog();
      }
    };
    document.addEventListener(COLOR_HOTSPOT_WILL_OPEN, this.#willOpenListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#willOpenListener) {
      document.removeEventListener(COLOR_HOTSPOT_WILL_OPEN, this.#willOpenListener);
      this.#willOpenListener = null;
    }
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  /**
   * Set up desktop event listeners (hover)
   * @returns {void}
   */
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;

    /** @type {() => void} */
    const pointerenterHandler = () => {
      if (dialog.open) return;

      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
    };

    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
    trigger.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  /**
   * Remove desktop event listeners from trigger
   * @returns {void}
   */
  #removeDesktopListeners() {
    const { trigger } = this.refs;

    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /**
   * Handle breakpoint changes
   * @returns {void}
   */
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();

    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  /**
   * Calculate the placement of the dialog.
   * @returns {Promise<void> | undefined}
   */
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;

    const hotspotsContainer = this.closest('.hotspots-container');

    if (!hotspotsContainer) {
      return;
    }

    const wasOpen = dialog.open;

    // When the dialog is closed, its bounding box reports 0 width/height in all browsers.
    // Temporarily show it in a hidden state so we can measure the real size needed
    // to decide whether it should render to the left, right, or centered.
    if (!wasOpen) {
      dialog.style.visibility = 'hidden';
      dialog.show();
    }

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;

    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    dialog.removeAttribute('data-placement');
    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;

    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    const x =
      spaceRight >= dialogWidth + BUTTON_GAP
        ? 'right'
        : spaceLeft >= dialogWidth + BUTTON_GAP
          ? 'left'
          : 'center';

    let y = 'bottom';
    let verticalOffset = 0;

    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;

      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';

        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        }
        y = 'bottom';
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;

    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }

    if (!wasOpen) {
      dialog.close();
      dialog.style.visibility = '';
    }

    return Promise.resolve();
  }

  /**
   * Handle pointer leave.
   * @param {PointerEvent} e - The event.
   * @returns {void}
   */
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;

    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    if (!dialog.open) return;

    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;

    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };

  /**
   * Handle hotspot click - on mobile/touch or desktop opens dialog.
   * @param {MouseEvent} e - The click event
   * @returns {void}
   */
  handleHotspotClick = (e) => {
    // If the real tap target was inside the dialog (e.g. the link), don’t handle – let the link work.
    if (isMobileBreakpoint() || isTouchDevice()) {
      const realTarget = e.composedPath?.()?.[0];
      if (realTarget instanceof Node && this.refs.dialog?.contains(realTarget)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    }
    this.showDialog();
  };

  showDialog = async () => {
    const { dialog, trigger } = this.refs;
    // Always notify other hotspots to close so only one popup is open at a time (desktop + mobile).
    document.dispatchEvent(
      new CustomEvent(COLOR_HOTSPOT_WILL_OPEN, { detail: { source: this } })
    );
    if (isMobileBreakpoint() || isTouchDevice()) {
      // So taps hit the dialog only: component (and trigger) ignore pointer, dialog receives them.
      this.style.pointerEvents = 'none';
      dialog.style.pointerEvents = 'auto';
    }
    await this.#calculateDialogPlacement();
    dialog.show();
    // Defer so the browser paints [open] with initial transition state before [data-showing='true']
    requestAnimationFrame(() => {
      dialog.dataset.showing = 'true';
    });
    document.body.addEventListener('click', this.lightDismissMouse);
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
    dialog.addEventListener('pointerleave', this.#handlePointerLeave);

    // Workaround: on mobile the tap often hits the trigger (theme’s click delegation) so the native
    // anchor never receives the event. We handle touchstart/click and navigate so the link works.
    // With multiple links, closest('a[href]') uses the link that was tapped; querySelector fallback
    // is for the current single-link layout where the tap hits the content div (sibling of the link).
    if (isMobileBreakpoint() || isTouchDevice()) {
      this.#dialogLinkTouchHandler = (e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target || !dialog.contains(target)) return;
        const link = target.closest?.('a[href]') ?? dialog.querySelector('a[href]');
        if (link instanceof HTMLAnchorElement) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = link.href;
        }
      };
      const touchOpts = { capture: true, passive: false };
      document.addEventListener('touchstart', this.#dialogLinkTouchHandler, touchOpts);

      this.#dialogLinkClickHandler = (e) => {
        const target = e.target instanceof Element ? e.target : null;
        if (!target || !dialog.contains(target)) return;
        const link = target.closest?.('a[href]') ?? dialog.querySelector('a[href]');
        if (link instanceof HTMLAnchorElement) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = link.href;
        }
      };
      dialog.addEventListener('click', this.#dialogLinkClickHandler, { capture: true });
    }

    if (isMobileBreakpoint() || isTouchDevice()) {
      document.body.classList.add(BODY_OVERLAY_OPEN_CLASS);
      const overlay = this.#getOverlay();
      if (overlay) {
        this.#overlayClickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeDialog();
        };
        overlay.addEventListener('click', this.#overlayClickHandler);
      }
    }
  };

  /**
   * Close the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;

    this.style.pointerEvents = '';
    dialog.style.pointerEvents = '';
    document.body.classList.remove(BODY_OVERLAY_OPEN_CLASS);
    const overlay = this.#getOverlay();
    if (overlay && this.#overlayClickHandler) {
      overlay.removeEventListener('click', this.#overlayClickHandler);
      this.#overlayClickHandler = null;
    }

    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    // Do not remove trigger's pointerleave – it was added in #setupDesktopListeners and must stay
    // so the popup hides when the pointer leaves the trigger/dialog.
    if (this.#dialogLinkTouchHandler) {
      const touchOpts = { capture: true, passive: false };
      document.removeEventListener('touchstart', this.#dialogLinkTouchHandler, touchOpts);
      this.#dialogLinkTouchHandler = null;
    }
    if (this.#dialogLinkClickHandler) {
      dialog.removeEventListener('click', this.#dialogLinkClickHandler, { capture: true });
      this.#dialogLinkClickHandler = null;
    }
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {MouseEvent} event - The event.
   * @returns {void}
   */
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Light dismiss the dialog.
   * @param {KeyboardEvent} event - The event.
   * @returns {void}
   */
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}

customElements.define('color-hotspot-component', ColorHotspotComponent);
